import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { building2, Upload, FileText, Download, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react'; // building2 is Building2 lowercased? Checking imports. 

// Lucide import check - standard imports are usually PascalCase
import { Building2 } from 'lucide-react';

export default function GenericStateTaxForm({
    stateCode,
    stateName,
    downloadUrl,
    employee,
    existingSubmission,
    onSubmit,
    onCancel
}) {
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                toast.error('Please upload a PDF file');
                return;
            }
            if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
                toast.error('File size must be less than 5MB');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleSubmit = async () => {
        if (!file && !existingSubmission) {
            toast.error('Please upload your completed tax form');
            return;
        }

        setIsSubmitting(true);
        try {
            // In a real app, upload file to storage here and get URL
            // Mocking file upload by converting to base64 for local storage (demo only)
            let fileData = existingSubmission?.data?.fileUrl;

            if (file) {
                const reader = new FileReader();
                fileData = await new Promise((resolve) => {
                    reader.onload = (e) => resolve(e.target.result);
                    reader.readAsDataURL(file);
                });
            }

            const submissionData = {
                state: stateCode,
                formName: stateName,
                fileUrl: fileData,
                fileName: file ? file.name : existingSubmission?.data?.fileName,
                uploadedAt: new Date().toISOString(),
                status: 'complete',
            };

            await onSubmit(submissionData);
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Failed to submit form');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card className="w-full max-w-2xl mx-auto border-0 shadow-none">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                        <CardTitle>{stateName} Withholding Certificate</CardTitle>
                        <CardDescription>
                            Upload your completed state tax withholding form for {stateCode}
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">

                {/* download instructions */}
                <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100">
                    <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="space-y-2">
                        <h4 className="font-medium text-blue-900">Step 1: Download Form</h4>
                        <p className="text-sm text-blue-700">
                            Please download and complete the official tax form for {stateName}.
                        </p>
                        {downloadUrl ? (
                            <Button variant="outline" size="sm" className="mt-2 bg-white text-blue-700 border-blue-200 hover:bg-blue-50" asChild>
                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="h-3 w-3 mr-2" />
                                    Download Official Form
                                </a>
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" className="mt-2 bg-white text-blue-700 border-blue-200 hover:bg-blue-50" asChild>
                                <a href={`https://www.google.com/search?q=${stateName}+employee+withholding+allowance+certificate+form+pdf`} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="h-3 w-3 mr-2" />
                                    Search for Form
                                </a>
                            </Button>
                        )}
                    </div>
                </div>

                {/* Upload Section */}
                <div className="bg-gray-50 p-4 rounded-lg flex items-start gap-3 border border-gray-200">
                    <Upload className="h-5 w-5 text-gray-600 mt-0.5" />
                    <div className="space-y-4 w-full">
                        <div>
                            <h4 className="font-medium text-gray-900">Step 2: Upload Completed Form</h4>
                            <p className="text-sm text-gray-500">
                                Upload the signed PDF copy of your form here.
                            </p>
                        </div>

                        <div className="flex items-center gap-4">
                            <input
                                type="file"
                                accept="application/pdf"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                                Select PDF File
                            </Button>
                            {file && (
                                <div className="flex items-center gap-2 text-sm text-green-600">
                                    <CheckCircle2 className="h-4 w-4" />
                                    {file.name}
                                </div>
                            )}
                            {!file && existingSubmission?.data?.fileName && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <FileText className="h-4 w-4" />
                                    Previously uploaded: {existingSubmission.data.fileName}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting || (!file && !existingSubmission)}>
                        {isSubmitting ? 'Uploading...' : 'Submit Form'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
